import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Copy, Check, ShieldCheck, AlertCircle, Package, ReceiptText } from "lucide-react";
import API from "../lib/api";

const fmtIDR = (v) => {
  const n = Number(v || 0);
  return `Rp ${Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const PAYMENT_LABEL = {
  paid: "Dibayar",
  pending: "Belum dibayar",
  unpaid: "Belum dibayar",
  failed: "Gagal",
  expired: "Kedaluwarsa",
  refunded: "Dikembalikan",
};

const paymentLabel = (status) => {
  const value = String(status || "pending").toLowerCase();
  return PAYMENT_LABEL[value] || value.replace(/_/g, " ");
};

const StatusBadge = ({ order }) => {
  const ps = (order.payment_status || "").toLowerCase();
  const st = (order.status || "").toLowerCase();
  const ds = (order.delivery_status || "").toLowerCase();

  let label = "Pending";
  let cls = "bg-yellow-500/15 text-yellow-300 border-yellow-500/30";
  if (ps === "paid" && st === "completed") {
    label = "Selesai";
    cls = "bg-green-500/15 text-green-300 border-green-500/30";
  } else if (ps === "paid" && ds === "shipped") {
    label = "Dikirim";
    cls = "bg-blue-500/15 text-blue-300 border-blue-500/30";
  } else if (ps === "paid" && ds === "disputed") {
    label = "Komplain";
    cls = "bg-red-500/15 text-red-300 border-red-500/30";
  } else if (ps === "paid") {
    label = "Dibayar";
    cls = "bg-cyan-500/15 text-cyan-300 border-cyan-500/30";
  } else if (ps === "expired" || ps === "failed") {
    label = ps.toUpperCase();
    cls = "bg-red-500/15 text-red-300 border-red-500/30";
  } else if (ps === "pending") {
    label = "Belum dibayar";
    cls = "bg-yellow-500/15 text-yellow-300 border-yellow-500/30";
  }
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${cls}`}>
      {label}
    </span>
  );
};

const CopyButton = ({ value, label = "Salin" }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value || ""));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // ignore
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      className="text-xs px-2 py-1 rounded border border-white/10 bg-white/5 hover:bg-white/10 text-white/80 inline-flex items-center gap-1"
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? "Tersalin" : label}
    </button>
  );
};

export default function OrderDetail() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await API.get(`/orders/${orderId}/detail`);
        const payload = res?.data;
        if (!payload || typeof payload !== "object" || !payload.order_id) {
          throw new Error("Data detail pesanan tidak valid");
        }
        if (alive) {
          setOrder({
            ...payload,
            items: Array.isArray(payload.items) ? payload.items : [],
          });
          setError("");
        }
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || e.message || "Gagal memuat pesanan");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b0b10] text-white flex items-center justify-center">
        <div className="text-white/60 text-sm">Memuat detail pesanan...</div>
      </div>
    );
  }
  if (error || !order) {
    return (
      <div className="min-h-screen bg-[#0b0b10] text-white p-6">
        <Link to="/account" className="inline-flex items-center gap-2 text-cyan-300 text-sm mb-4">
          <ChevronLeft size={16} /> Kembali
        </Link>
        <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl p-4">
          {error || "Pesanan tidak ditemukan"}
        </div>
      </div>
    );
  }

  const items = Array.isArray(order.items) ? order.items : [];
  const totalQty = items.reduce((sum, it) => sum + Number(it.quantity || 0), 0);
  const productPrice = Number(order.subtotal_idr || 0);
  const adminFee = Number(order.admin_fee_idr || 0);
  const total = Number(order.total_idr || 0);

  return (
    <div className="min-h-screen bg-[#0b0b10] text-white">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/account" className="inline-flex items-center gap-1 text-cyan-300 text-sm">
            <ChevronLeft size={16} /> Kembali ke Akun
          </Link>
          <StatusBadge order={order} />
        </div>

        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-[#13131a] to-[#0d0d14] border border-white/10 rounded-2xl p-5 sm:p-6 mb-4"
          data-testid="order-detail-summary"
        >
          <div className="flex items-center gap-2 mb-5">
            <ReceiptText size={18} className="text-cyan-300" />
            <h1 className="font-display text-xl sm:text-2xl font-black uppercase tracking-tight">Detail Pesanan</h1>
          </div>

          <dl className="divide-y divide-white/10 border-y border-white/10">
            <div className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4">
              <dt className="text-xs uppercase tracking-widest text-white/45">Order ID</dt>
              <dd className="flex items-center gap-2 sm:justify-end min-w-0">
                <span className="font-mono text-sm break-all" data-testid="detail-order-id">{order.order_id}</span>
                <CopyButton value={order.order_id} />
              </dd>
            </div>
            <div className="py-3 flex items-center justify-between gap-4">
              <dt className="text-xs uppercase tracking-widest text-white/45">Total Order</dt>
              <dd className="font-display font-black text-lg text-cyan-300" data-testid="detail-total-order">{fmtIDR(total)}</dd>
            </div>
            <div className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4">
              <dt className="text-xs uppercase tracking-widest text-white/45">Tgl Transaksi</dt>
              <dd className="text-sm text-white/90 sm:text-right" data-testid="detail-transaction-date">{fmtDate(order.created_at)}</dd>
            </div>
            <div className="py-3 flex items-center justify-between gap-4">
              <dt className="text-xs uppercase tracking-widest text-white/45">Status Pembayaran</dt>
              <dd className="text-sm font-bold uppercase text-green-300" data-testid="detail-payment-status">{paymentLabel(order.payment_status)}</dd>
            </div>
          </dl>

          {(order.payment_method || order.paid_at) && (
            <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm">
              <div><span className="text-white/40">Metode:</span> <span className="text-white/80">{order.payment_method || "—"}</span></div>
              <div className="sm:text-right"><span className="text-white/40">Dibayar:</span> <span className="text-white/80">{fmtDate(order.paid_at)}</span></div>
            </div>
          )}
        </motion.section>

        {/* Price summary */}
        <section className="bg-[#13131a] border border-white/10 rounded-2xl p-5 sm:p-6 mb-4" data-testid="order-price-detail">
          <h2 className="text-sm font-semibold text-white/90 mb-3">Rincian Pembayaran</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-white/70">
              <span>Harga</span>
              <span data-testid="detail-price">{fmtIDR(productPrice)}</span>
            </div>
            {order.discount_idr > 0 && (
              <div className="flex justify-between text-green-300">
                <span>Diskon</span>
                <span>− {fmtIDR(order.discount_idr)}</span>
              </div>
            )}
            <div className="flex justify-between text-white/70">
              <span>Biaya admin</span>
              <span data-testid="detail-admin-fee">{fmtIDR(adminFee)}</span>
            </div>
            <div className="border-t border-white/10 my-2" />
            <div className="flex justify-between text-base font-semibold text-white">
              <span>Total</span>
              <span className="text-cyan-300" data-testid="detail-total">{fmtIDR(total)}</span>
            </div>
          </div>
        </section>

        {/* Products */}
        <section className="bg-[#13131a] border border-white/10 rounded-2xl p-5 sm:p-6 mb-4" data-testid="order-product-detail">
          <div className="flex items-center gap-2 mb-4">
            <Package size={16} className="text-cyan-300" />
            <h2 className="text-sm font-semibold text-white/90">Detail Produk</h2>
            <span className="text-xs text-white/50">({items.length} produk · {totalQty} qty)</span>
          </div>
          {items.length === 0 ? (
            <div className="border border-white/10 rounded-xl p-4 text-sm text-white/50">Data produk tidak tersedia.</div>
          ) : (
            <div className="space-y-4">
              {items.map((it, idx) => {
                const codes = Array.isArray(it.delivered_codes) ? it.delivered_codes : [];
                return (
                  <div key={`${it.product_id || "product"}_${idx}`} className="border border-white/10 rounded-xl p-3">
                    <div className="flex items-start gap-3">
                      {it.image_url ? (
                        <img src={it.image_url} alt={it.title || "Produk"} className="w-14 h-14 rounded-lg object-cover border border-white/10" />
                      ) : (
                        <div className="w-14 h-14 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center"><Package size={20} className="text-white/20" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-white/40">Nama Produk</div>
                        <div className="text-sm font-semibold text-white break-words" data-testid={`detail-product-name-${idx}`}>{it.title || "Produk"}</div>
                        <div className="text-xs text-white/50 mt-1">{Number(it.quantity || 0)} × {fmtIDR(it.price)}</div>
                        <div className="text-sm text-cyan-300 mt-1">{fmtIDR(it.subtotal)}</div>
                      </div>
                    </div>
                    {(it.has_digital_codes || codes.length > 0) && (
                      <div className="mt-3 space-y-2">
                        <div className="text-xs text-white/50">Kode voucher/akun:</div>
                        {codes.length === 0 ? (
                          <div className="text-xs text-yellow-300/80">Kode belum tersedia — akan dikirim setelah seller konfirmasi.</div>
                        ) : codes.map((c, i) => {
                          const code = typeof c === "string" ? c : c?.code;
                          return (
                            <div key={`${c?.id || code || "code"}_${i}`} className="flex items-center justify-between gap-3 bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2">
                              <code className="text-cyan-200 text-sm font-mono break-all">{code || "—"}</code>
                              {code && <CopyButton value={code} />}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Garansi / dispute info */}
        {order.warranty_until && order.delivery_status === "shipped" && !order.escrow_released && (
          <div className="bg-blue-500/5 border border-blue-500/20 rounded-2xl p-4 mb-4 flex items-start gap-3">
            <ShieldCheck size={18} className="text-blue-300 mt-0.5" />
            <div className="text-sm text-blue-100">
              Pesanan dikirim. Garansi sampai <strong>{fmtDate(order.warranty_until)}</strong>. Setelah lewat, dana otomatis cair ke seller.
            </div>
          </div>
        )}
        {order.delivery_status === "disputed" && order.dispute_reason && (
          <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-4 mb-4 flex items-start gap-3">
            <AlertCircle size={18} className="text-red-300 mt-0.5" />
            <div className="text-sm text-red-100">
              <div className="font-semibold mb-1">Komplain terbuka</div>
              <div className="text-red-200/80">{order.dispute_reason}</div>
            </div>
          </div>
        )}
        {order.escrow_released && (
          <div className="bg-green-500/5 border border-green-500/20 rounded-2xl p-4 mb-4 flex items-start gap-3">
            <Check size={18} className="text-green-300 mt-0.5" />
            <div className="text-sm text-green-100">
              Dana sudah dicairkan ke seller. Pesanan selesai.
            </div>
          </div>
        )}

        {/* Reference */}
        {(order.merchant_ref || order.tripay_reference) && (
          <div className="text-xs text-white/40 text-center mt-6 space-y-1">
            {order.merchant_ref && <div>Merchant Ref: <span className="font-mono">{order.merchant_ref}</span></div>}
            {order.tripay_reference && <div>Tripay Ref: <span className="font-mono">{order.tripay_reference}</span></div>}
          </div>
        )}
      </div>
    </div>
  );
}
